"use client";

import * as React from "react";
import {
  flexRender,
  type Table as TanStackTable,
  type Row,
} from "@tanstack/react-table";
import { cn } from "@repo/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

interface DataTableProps<TData> {
  table: TanStackTable<TData>;
  maxHeight?: string;
  minWidth?: string;
  getRowClassName?: (row: Row<TData>) => string;
  getRowTestId?: (row: Row<TData>) => string | undefined;
  /**
   * Makes rows clickable. Clicks originating from interactive elements
   * inside a cell (buttons, links, inputs, menus) are ignored so row
   * actions keep working.
   */
  onRowClick?: (row: Row<TData>) => void;
}

const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="menuitem"], [role="menu"], [role="checkbox"], [data-no-row-click]';

/** Check if a column definition has `meta.sticky === "right"` */
function isStickyRight(columnDef: { meta?: unknown }): boolean {
  const meta = columnDef.meta as { sticky?: string } | undefined;
  return meta?.sticky === "right";
}

export function DataTable<TData>({
  table,
  maxHeight = "calc(100vh - 320px)",
  minWidth,
  getRowClassName,
  getRowTestId,
  onRowClick,
}: DataTableProps<TData>) {
  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    row: Row<TData>
  ) => {
    if (!onRowClick) return;
    const target = event.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) return;
    onRowClick(row);
  };

  return (
    <div className="rounded-xl border bg-surface overflow-hidden">
      <div className="overflow-auto" style={{ maxHeight }}>
        <Table style={minWidth ? { minWidth } : undefined}>
          <TableHeader className="sticky top-0 z-10 [&_tr]:border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sticky = isStickyRight(header.column.columnDef);
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        sticky &&
                          "sticky right-0 z-20 bg-table-header border-l border-border/50"
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    onRowClick && "cursor-pointer",
                    getRowClassName?.(row)
                  )}
                  data-testid={getRowTestId?.(row)}
                  onClick={(event) => handleRowClick(event, row)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const sticky = isStickyRight(cell.column.columnDef);
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          sticky &&
                            "sticky right-0 bg-surface border-l border-border/50"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
